import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Form, InputGroup, Pagination, Spinner, Dropdown } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import ExternalRecipientBadge from "../../components/forms/ExternalRecipientBadge";
import "./ReportsPage.css";

// Phase 5, §25/§26/§33. One page, category tabs — never a generic SQL
// report builder. Export re-uses the exact same report_code + filters the
// live view is showing, so what you see is what you export.
const REPORT_CATEGORIES = [
  { code: "tasks", label: "Tasks" },
  { code: "task-tat", label: "TAT" },
  { code: "task-contributions", label: "Performer Contribution" },
  { code: "task-delegations", label: "Delegation" },
  { code: "forms", label: "Forms" },
  { code: "form-results", label: "Form Results" },
  { code: "form-reviews", label: "Form Reviews" },
  { code: "uat", label: "UAT" },
  { code: "uat-defects", label: "UAT Defects" },
  { code: "task-approvals", label: "Task Approvals" },
  { code: "workload", label: "Workload" },
];

const REPORT_ENDPOINTS = {
  tasks: "/reports/tasks", "task-tat": "/reports/task-tat", "task-contributions": "/reports/task-contributions",
  "task-delegations": "/reports/task-delegations", forms: "/reports/forms", "form-results": "/reports/form-results",
  "form-reviews": "/reports/form-reviews", uat: "/reports/uat", "uat-defects": "/reports/uat-defects",
  "task-approvals": "/reports/task-approvals", workload: "/reports/workload",
};

// Presentation-only helpers for the live-view table below. Columns are
// derived at runtime from Object.keys(rows[0]) and vary per report tab, so
// these work off the cell's own value/key shape rather than any fixed
// column list. Nothing here touches `rows`/`filters` state, API params, or
// what gets exported -- purely how a cell is rendered on screen.
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

// "2026-09-21T12:00:00.000Z" -> "21 Sep 2026, 12:00 PM"
const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  }).format(date);
};

// "in_progress" -> "In Progress"
const formatStatusLabel = (value) =>
  String(value).replace(/[_-]+/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());

// Backend enum values seen across the report endpoints, grouped into the
// same success/warning/danger tones used elsewhere in the app. Anything
// not listed here still renders as a readable pill (neutral tone) rather
// than falling back to plain text.
const STATUS_TONE = {
  completed: "success", closed: "success", approved: "success", resolved: "success", passed: "success", pass: "success", active: "success",
  pending: "warning", in_progress: "warning", "in-progress": "warning", open: "warning", draft: "warning", in_review: "warning",
  rejected: "danger", failed: "danger", fail: "danger", cancelled: "danger", canceled: "danger", blocked: "danger", inactive: "danger", overdue: "danger", escalated: "danger",
};

const renderReportCell = (key, value) => {
  // Phase 65 follow-up: form reports flag EXTERNAL recipients (email only,
  // no portal account) -- same badge as every other form list.
  if (key === "is_external") return value ? <ExternalRecipientBadge external className="ms-0" /> : "—";
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" && ISO_DATETIME_RE.test(value)) return formatDateTime(value);
  if (/status$/i.test(key) && typeof value === "string") {
    const tone = STATUS_TONE[value.toLowerCase()] || "neutral";
    return <span className={`unit-status reports-status-badge reports-status-${tone}`}>{formatStatusLabel(value)}</span>;
  }
  return String(value);
};

export default function ReportsPage() {
  const [category, setCategory] = useState("tasks");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const [filters, setFilters] = useState({ start_date: "", end_date: "", project_id: "", department_id: "", status: "", priority: "", search: "" });

  const columns = useMemo(() => (rows.length ? Object.keys(rows[0]) : []), [rows]);

  // Live-view headers only (export column labels are already defined
  // server-side in reportExportService.js's REPORT_REGISTRY) -- a plain
  // snake_case -> Title Case pass so new columns like sla_status/
  // resolution_hours read as "Sla Status"/"Resolution Hours" rather than
  // verbatim raw keys.
  const prettifyKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), page: currentPage, pageSize };
      const res = await api.get(REPORT_ENDPOINTS[category], { params });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load report");
      if (err.response?.status === 403) setError("You do not have permission to view this report.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [category, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  const runExport = async (format) => {
    try {
      const payload = { report_code: category, format, filters: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const res = await api.post("/reports/export", payload);
      const exportId = res.data?.data?.auditId;
      toast.success(res.data?.message || "Export ready");
      if (exportId) {
        // A plain <a href> navigation won't send the Authorization header,
        // so the file is fetched as a blob through the authenticated axios
        // instance and downloaded from there instead.
        const fileRes = await api.get(`/reports/export/${exportId}/download`, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([fileRes.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `${category}.${format.toLowerCase()}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Export failed");
    }
  };

  return (
    <div className="unit-page reportspage-theme">
      <div className="unit-header">
        <div className="reports-heading-content">
          <div className="reports-heading-icon">
            <i className="fa-solid fa-chart-column" aria-hidden="true"></i>
          </div>
          <div>
            <div className="unit-page-label">Reports</div>
            <h2>Reports</h2>
            <p>Every report applies the same backend data scope as its export — nothing exported can exceed what you can already see here.</p>
          </div>
        </div>
        <div className="unit-header-actions">
          <Dropdown>
            <Dropdown.Toggle className="unit-btn unit-btn-primary reports-export-toggle">
              <i className="fa-solid fa-file-export" aria-hidden="true"></i>
              Export
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => runExport("CSV")}>CSV</Dropdown.Item>
              <Dropdown.Item onClick={() => runExport("XLSX")}>XLSX</Dropdown.Item>
              <Dropdown.Item onClick={() => runExport("PDF")}>PDF</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="unit-tabs reports-tabs">
        {REPORT_CATEGORIES.map((c) => (
          <Button
            key={c.code}
            size="sm"
            variant={category === c.code ? "primary" : "outline-secondary"}
            className={`unit-tab${category === c.code ? " unit-tab-active" : ""}`}
            onClick={() => { setCategory(c.code); setCurrentPage(1); }}
          >
            {c.label}
          </Button>
        ))}
      </div>

      <div className="unit-card">
        <div className="unit-toolbar reports-filter-toolbar">
          <Form.Control size="sm" type="date" className="reports-filter-date" aria-label="Date from" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
          <Form.Control size="sm" type="date" className="reports-filter-date" aria-label="Date to" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
          <Form.Control size="sm" placeholder="Project ID" className="reports-filter-project" value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} />
          <Form.Select size="sm" className="reports-filter-priority" value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} aria-label="Priority">
            <option value="">All Priorities</option>
            <option value="high">Critical / High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </Form.Select>
          <InputGroup size="sm" className="unit-search reports-filter-search">
            <InputGroup.Text aria-hidden="true"><i className="fa-solid fa-magnifying-glass"></i></InputGroup.Text>
            <Form.Control placeholder="Search..." aria-label="Search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
          </InputGroup>
          <Button className="unit-btn unit-btn-light reports-apply-btn" onClick={() => { setCurrentPage(1); fetchReport(); }}>
            <i className="fa-solid fa-filter" aria-hidden="true"></i>
            Apply Filters
          </Button>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader"><Spinner animation="border" /><p>Loading report...</p></div>
          ) : error ? (
            <div className="unit-empty reports-empty-error">
              <div className="unit-empty-icon reports-empty-icon-error"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i></div>
              <h5>{error}</h5>
            </div>
          ) : rows.length === 0 ? (
            <div className="unit-empty">
              <div className="unit-empty-icon"><i className="fa-solid fa-chart-column" aria-hidden="true"></i></div>
              <h5>No data found</h5>
              <p>Try another filter or category.</p>
            </div>
          ) : (
            <Table responsive size="sm" className="unit-table">
              <thead><tr>{columns.map((c) => <th key={c}>{prettifyKey(c)}</th>)}</tr></thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.id ?? i}>
                    {columns.map((c) => <td key={c}>{renderReportCell(c, row[c])}</td>)}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">Showing <strong>{rows.length}</strong> of <strong>{total}</strong> row(s)</div>
          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} />
              <Pagination.Item active>{currentPage}</Pagination.Item>
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} />
            </Pagination>
          )}
        </div>
      </div>
    </div>
  );
}
